import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import {
  CheckSquare, WarningCircle, Target, ListChecks, ArrowRight, Warning,
  ClipboardText, Package,
} from "@phosphor-icons/react";

const BUCKET_META = {
  approvals:  { icon: CheckSquare,  label: "Approvals",  accent: "#0284c7", empty: "No pending approvals" },
  alerts:     { icon: WarningCircle,label: "Alerts",     accent: "#dc2626", empty: "All systems normal" },
  exceptions: { icon: Target,       label: "Exceptions", accent: "#d97706", empty: "No exceptions today" },
  tasks:      { icon: ListChecks,   label: "Your tasks", accent: "#7c3aed", empty: "No tasks assigned to you" },
};

const PRIO_DOT = {
  high: "bg-[#dc2626]",
  med:  "bg-[#d97706]",
  low:  "bg-[#a1a1aa]",
  info: "bg-[#0284c7]",
};

export default function AttentionStrip() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/attention");
        if (!cancelled) setData(data);
      } catch (e) { /* silent */ }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading || !data) {
    return (
      <div className="phizo-card p-4 mb-6" data-testid="attention-strip-loading">
        <div className="phizo-label text-[10px] text-[#a1a1aa]">Loading what needs your attention…</div>
      </div>
    );
  }

  const buckets = [
    { key: "approvals",  items: data.approvals  },
    { key: "exceptions", items: data.exceptions },
    { key: "alerts",     items: data.alerts     },
    { key: "tasks",      items: data.tasks      },
  ];
  const totalOpen = buckets.reduce((n, b) => n + (b.items?.length || 0), 0);

  return (
    <section
      className="mb-6 border border-[#e4e4e7] bg-white overflow-hidden"
      data-testid="attention-strip"
    >
      <div className="flex items-center justify-between px-5 py-3 border-b border-[#e4e4e7] bg-[#fafafa]">
        <div>
          <div className="phizo-label text-[10px] text-[#a1a1aa]">Home</div>
          <h2 className="font-heading text-xl font-black tracking-tighter">
            What needs your attention?
          </h2>
        </div>
        <div className="text-right">
          <div className="mono font-black text-2xl leading-none">{totalOpen}</div>
          <div className="phizo-label text-[10px] text-[#a1a1aa] mt-0.5">open items</div>
        </div>
      </div>

      {totalOpen === 0 ? (
        <div className="p-10 text-center text-sm text-[#71717a]" data-testid="attention-empty">
          🎉 You're all clear. Nothing needs your attention right now.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-0 divide-x divide-[#e4e4e7]">
          {buckets.map(({ key, items }) => {
            const meta = BUCKET_META[key];
            const Icon = meta.icon;
            const count = items?.length || 0;
            return (
              <div key={key} className="p-4 min-h-[180px]" data-testid={`attention-bucket-${key}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Icon size={16} weight="bold" style={{ color: meta.accent }} />
                    <span className="phizo-label text-[10px] tracking-wider">{meta.label}</span>
                  </div>
                  <span
                    className="mono text-xs font-bold px-1.5 py-0.5"
                    style={{ background: count ? meta.accent : "#e4e4e7",
                             color: count ? "white" : "#71717a" }}
                    data-testid={`attention-count-${key}`}
                  >
                    {count}
                  </span>
                </div>
                {count === 0 ? (
                  <div className="text-xs text-[#a1a1aa]">{meta.empty}</div>
                ) : (
                  <ul className="space-y-2">
                    {items.slice(0, 4).map((it, idx) => (
                      <li key={`${key}-${idx}`}>
                        <button
                          type="button"
                          onClick={() => it.url && nav(it.url)}
                          className="w-full text-left group"
                          data-testid={`attention-item-${key}-${idx}`}
                        >
                          <div className="flex items-start gap-2">
                            <span className={`w-1.5 h-1.5 rounded-full mt-1.5 shrink-0 ${PRIO_DOT[it.priority] || PRIO_DOT.info}`} />
                            <div className="flex-1 min-w-0">
                              <div className="text-xs font-semibold truncate group-hover:underline">
                                {it.title}
                              </div>
                              {it.subtitle && (
                                <div className="text-[11px] text-[#71717a] truncate">
                                  {it.subtitle}
                                </div>
                              )}
                            </div>
                            <ArrowRight
                              size={12} weight="bold"
                              className="text-[#a1a1aa] group-hover:text-black mt-1 shrink-0"
                            />
                          </div>
                        </button>
                      </li>
                    ))}
                    {items.length > 4 && (
                      <li className="text-[11px] text-[#71717a] pl-3.5">
                        +{items.length - 4} more
                      </li>
                    )}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
